import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, LogOut, RefreshCcw, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const STORAGE_KEY = 'admin_services_password';

const DEFAULT_PAYMENT_SETTINGS = {
  enableManualPayments: true,
  manualPaymentInstructions:
    'Manual payment is available after booking confirmation (for example bank transfer or cash).',
  enablePaypalPayments: false,
  paypalClientId: '',
  paypalEnvironment: 'sandbox',
  paypalCurrency: 'EUR',
  onlinePaymentTaxPercent: 19,
  onlinePaymentBookingFee: 2.5,
  onlinePaymentMinimumPercent: 50,
  cancellationWindowHours: 24,
  cancellationTerms:
    'A minimum booking payment of 50% is required when booking online, or you may pay the full amount. If a booking is cancelled within the cancellation window, no refund is provided.',
  bookingBufferMinutes: 60,
};

const normalizeCurrency = (value, fallback = 'EUR') => {
  const cleaned = String(value || '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3);

  return cleaned || fallback;
};

const normalizeNonNegativeNumber = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.round(parsed * 100) / 100;
};

const normalizePaymentSettings = (payload = {}) => ({
  enableManualPayments: Boolean(payload.enableManualPayments),
  manualPaymentInstructions: String(payload.manualPaymentInstructions || '').trim(),
  enablePaypalPayments: Boolean(payload.enablePaypalPayments),
  paypalClientId: String(payload.paypalClientId || '').trim(),
  paypalEnvironment:
    String(payload.paypalEnvironment || 'sandbox').trim().toLowerCase() === 'live'
      ? 'live'
      : 'sandbox',
  paypalCurrency: normalizeCurrency(payload.paypalCurrency, 'EUR'),
  onlinePaymentTaxPercent: normalizeNonNegativeNumber(payload.onlinePaymentTaxPercent, 19),
  onlinePaymentBookingFee: normalizeNonNegativeNumber(payload.onlinePaymentBookingFee, 2.5),
  onlinePaymentMinimumPercent: Math.min(
    100,
    Math.max(50, normalizeNonNegativeNumber(payload.onlinePaymentMinimumPercent, 50)),
  ),
  cancellationWindowHours: Math.max(
    1,
    Math.round(normalizeNonNegativeNumber(payload.cancellationWindowHours, 24)),
  ),
  cancellationTerms: String(payload.cancellationTerms || '').trim(),
  bookingBufferMinutes: Math.max(
    0,
    Math.round(normalizeNonNegativeNumber(payload.bookingBufferMinutes, 60)),
  ),
});

const AdminPayments = () => {
  const [paymentSettings, setPaymentSettings] = useState(DEFAULT_PAYMENT_SETTINGS);
  const [siteSettingsPayload, setSiteSettingsPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  const loadPaymentSettings = useCallback(async () => {
    setIsLoading(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/site-settings', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load payment settings (${response.status})`);
      }

      const payload = await response.json();
      setSiteSettingsPayload(payload);
      setPaymentSettings(normalizePaymentSettings(payload));
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to load payment settings. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPaymentSettings();
    }
  }, [isAuthenticated, loadPaymentSettings]);

  const updatePaymentField = (field, value) => {
    setPaymentSettings((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const savePaymentSettings = async () => {
    if (!siteSettingsPayload || typeof siteSettingsPayload !== 'object') {
      setStatus({
        type: 'error',
        text: 'Payment settings are not loaded yet. Click Reload and try again.',
      });
      return;
    }

    const normalizedPayment = normalizePaymentSettings(paymentSettings);
    if (!normalizedPayment.enableManualPayments && !normalizedPayment.enablePaypalPayments) {
      setStatus({
        type: 'error',
        text: 'Enable at least one payment method (manual or PayPal).',
      });
      return;
    }

    if (normalizedPayment.enableManualPayments && !normalizedPayment.manualPaymentInstructions) {
      setStatus({
        type: 'error',
        text: 'Manual payment instructions are required when manual payments are enabled.',
      });
      return;
    }

    if (normalizedPayment.enablePaypalPayments && !normalizedPayment.paypalClientId) {
      setStatus({
        type: 'error',
        text: 'PayPal Client ID is required when PayPal payments are enabled.',
      });
      return;
    }

    if (!normalizedPayment.cancellationTerms) {
      setStatus({
        type: 'error',
        text: 'Cancellation terms are required.',
      });
      return;
    }

    setIsSaving(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/site-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({
          ...siteSettingsPayload,
          ...normalizedPayment,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        throw new Error(`Failed to save payment settings (${response.status})`);
      }

      const payload = await response.json();
      setSiteSettingsPayload(payload);
      setPaymentSettings(normalizePaymentSettings(payload));
      setStatus({ type: 'success', text: 'Payment settings saved successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to save payment settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setAdminPassword('');
    setPaymentSettings(DEFAULT_PAYMENT_SETTINGS);
    setSiteSettingsPayload(null);
    setStatus({ type: '', text: '' });
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Access
              </CardTitle>
              <CardDescription>Enter the admin password to manage payments.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-12">
      <div className="container mx-auto px-4 max-w-5xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Payments Admin</h1>
            <p className="text-sage-600 mt-1">
              Configure payment methods, booking fees, tax, cancellation rules, and post-booking buffer.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-sage-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Home
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={loadPaymentSettings}
              disabled={isLoading || isSaving}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              type="button"
              onClick={savePaymentSettings}
              disabled={isLoading || isSaving}
              className="bg-sage-600 hover:bg-sage-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payments
            </CardTitle>
            <CardDescription>
              Configure manual and PayPal checkout options, tax, and online booking fee.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 bg-white pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-2 rounded-md border border-sage-200 px-3 py-2">
                <input
                  type="checkbox"
                  checked={Boolean(paymentSettings.enableManualPayments)}
                  onChange={(event) =>
                    updatePaymentField('enableManualPayments', event.target.checked)
                  }
                />
                <span className="text-sage-800 text-sm">Enable manual payments</span>
              </label>

              <label className="flex items-center gap-2 rounded-md border border-sage-200 px-3 py-2">
                <input
                  type="checkbox"
                  checked={Boolean(paymentSettings.enablePaypalPayments)}
                  onChange={(event) =>
                    updatePaymentField('enablePaypalPayments', event.target.checked)
                  }
                />
                <span className="text-sage-800 text-sm">Enable PayPal payments</span>
              </label>
            </div>

            <div>
              <Label className="text-sage-700">Manual Payment Instructions</Label>
              <Textarea
                value={paymentSettings.manualPaymentInstructions}
                onChange={(event) =>
                  updatePaymentField('manualPaymentInstructions', event.target.value)
                }
                className="mt-1.5 border-sage-300 min-h-24"
              />
              <p className="text-xs text-sage-500 mt-1">
                Shown to users when manual payment is selected.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sage-700">PayPal Client ID</Label>
                <Input
                  value={paymentSettings.paypalClientId}
                  onChange={(event) => updatePaymentField('paypalClientId', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">PayPal Environment</Label>
                <select
                  value={paymentSettings.paypalEnvironment}
                  onChange={(event) =>
                    updatePaymentField('paypalEnvironment', event.target.value)
                  }
                  className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="sandbox">sandbox</option>
                  <option value="live">live</option>
                </select>
              </div>
            </div>

            <div className="max-w-xs">
              <Label className="text-sage-700">PayPal Currency</Label>
              <Input
                value={paymentSettings.paypalCurrency}
                onChange={(event) => updatePaymentField('paypalCurrency', event.target.value)}
                className="mt-1.5 border-sage-300"
                placeholder="EUR"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Online Payment Tax (%)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentSettings.onlinePaymentTaxPercent}
                  onChange={(event) =>
                    updatePaymentField('onlinePaymentTaxPercent', event.target.value)
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Online Booking Fee (flat)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentSettings.onlinePaymentBookingFee}
                  onChange={(event) =>
                    updatePaymentField('onlinePaymentBookingFee', event.target.value)
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Minimum Booking Payment (%)</Label>
                <Input
                  type="number"
                  min="50"
                  max="100"
                  step="0.01"
                  value={paymentSettings.onlinePaymentMinimumPercent}
                  onChange={(event) =>
                    updatePaymentField('onlinePaymentMinimumPercent', event.target.value)
                  }
                  className="mt-1.5 border-sage-300"
                />
                <p className="text-xs text-sage-500 mt-1">
                  Customers must pay at least this percentage now, or pay full.
                </p>
              </div>
              <div>
                <Label className="text-sage-700">Cancellation Window (hours)</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={paymentSettings.cancellationWindowHours}
                  onChange={(event) =>
                    updatePaymentField('cancellationWindowHours', event.target.value)
                  }
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="max-w-sm">
              <Label className="text-sage-700">Post-Booking Buffer (minutes)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={paymentSettings.bookingBufferMinutes}
                onChange={(event) =>
                  updatePaymentField('bookingBufferMinutes', event.target.value)
                }
                className="mt-1.5 border-sage-300"
              />
              <p className="text-xs text-sage-500 mt-1">
                Added after each confirmed/completed booking to prevent overlap.
              </p>
            </div>

            <div>
              <Label className="text-sage-700">Cancellation Terms</Label>
              <Textarea
                value={paymentSettings.cancellationTerms}
                onChange={(event) =>
                  updatePaymentField('cancellationTerms', event.target.value)
                }
                className="mt-1.5 border-sage-300 min-h-24"
              />
              <p className="text-xs text-sage-500 mt-1">
                Shown on booking checkout and invoice PDF.
              </p>
            </div>

            <p className="text-xs text-sage-500">
              Security: set backend env var <code>PAYPAL_CLIENT_SECRET</code> in <code>backend/.env</code>.
              It is not exposed in public site settings.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPayments;
